import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import { fetchDashboard } from '../../api/dashboard';
import {
  fetchPendingApprovals,
  fetchWorkspaceStats,
  type PendingApprovalsResult,
  type WorkspaceStats,
} from '../../data/home';
import {
  fetchPendingAgreementApprovals,
  type PendingAgreementApproval,
} from '../../data/serviceAgreements';
import supabaseClient from '../../lib/supabaseClient';
import {
  isAgreementApprovalStepForViewer,
  isOverrideApprovalStep,
  type AgreementApprovalViewer,
} from '../service';

/** Shared by NotificationBell and HomePage so dismiss/read in one place updates the other. */
export const dashboardQueryKey: QueryKey = ['dashboard', 'payload'];

export const getDashboardQueryOptions = ({ enabled = true }: { enabled?: boolean }) => ({
  queryKey: dashboardQueryKey,
  enabled,
  queryFn: fetchDashboard,
  refetchInterval: () => {
    if (typeof document === 'undefined') return 60000;
    return document.hidden ? false : 60000;
  },
    refetchOnWindowFocus: false,
});

export const useDashboardQuery = (
  options: { enabled?: boolean } = {},
): UseQueryResult<any, Error> => useQuery(getDashboardQueryOptions(options));

export const workspaceStatsQueryKey: QueryKey = ['workspace', 'stats'];

export const getWorkspaceStatsQueryOptions = ({
  supabase = supabaseClient,
  enabled = true,
  includeEvents = true,
  includeCustomers = true,
  includeVenues = true,
  includeUsers = false,
}: {
  supabase?: SupabaseClient;
  enabled?: boolean;
  includeEvents?: boolean;
  includeCustomers?: boolean;
  includeVenues?: boolean;
  includeUsers?: boolean;
}) => ({
  queryKey: [...workspaceStatsQueryKey, { includeEvents, includeCustomers, includeVenues, includeUsers }],
  enabled,
  queryFn: async () => {
    const { data, error } = await fetchWorkspaceStats({
      supabase,
      includeEvents,
      includeCustomers,
      includeVenues,
      includeUsers,
    });
    if (error) throw error;
    return data;
  },
  staleTime: 30000,
});

export const useWorkspaceStatsQuery = (
  options: {
    supabase?: SupabaseClient;
    enabled?: boolean;
    includeEvents?: boolean;
    includeCustomers?: boolean;
    includeVenues?: boolean;
    includeUsers?: boolean;
  },
): UseQueryResult<WorkspaceStats, PostgrestError> => useQuery(getWorkspaceStatsQueryOptions(options));

export const pendingApprovalsQueryKey = (profileId: string | null | undefined): QueryKey => [
  'approvals',
  'pending',
  profileId ?? null,
];

export const getPendingApprovalsQueryOptions = ({
  profileId,
  supabase = supabaseClient,
  enabled = true,
}: {
  profileId: string | null | undefined;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: pendingApprovalsQueryKey(profileId),
  enabled: enabled && Boolean(profileId),
  queryFn: async () => {
    const { data, error } = await fetchPendingApprovals(profileId as string, { supabase });
    if (error) throw error;
    return data;
  },
  staleTime: 15000,
  refetchInterval: () => {
    if (typeof document === 'undefined') return 60000;
    return document.hidden ? false : 60000;
  },
});

export const usePendingApprovalsQuery = (
  options: { profileId: string | null | undefined; supabase?: SupabaseClient; enabled?: boolean },
): UseQueryResult<PendingApprovalsResult, PostgrestError> => useQuery(getPendingApprovalsQueryOptions(options));

/** Service agreement quotes waiting on the viewer; invalidate after approving one. */
export const pendingAgreementApprovalsQueryKey: QueryKey = ['approvals', 'service-agreements'];

export type PendingAgreementApprovalItem = {
  quoteId: string;
  clientName: string | null;
  tierName: string | null;
  version: number;
  price: number | null;
  requestedAt: string | null;
  override: boolean;
  policyName: string | null;
};

// One card per quote, oldest first. Submitting a quote stamps updated_at, and nothing else
// writes the quote while it waits, so updated_at is when approval was requested.
function toPendingAgreementApprovalItems(
  rows: PendingAgreementApproval[],
  viewer: AgreementApprovalViewer,
): PendingAgreementApprovalItem[] {
  const byQuote = new Map<string, PendingAgreementApprovalItem>();
  for (const row of rows) {
    if (!row.quote || !isAgreementApprovalStepForViewer(row, viewer)) continue;
    const current = byQuote.get(row.quote.quote_id);
    const override = isOverrideApprovalStep(row);
    byQuote.set(row.quote.quote_id, {
      quoteId: row.quote.quote_id,
      clientName: row.quote.client?.business_name ?? null,
      tierName: row.quote.tier_name ?? null,
      version: row.quote.version,
      price: row.quote.price == null ? null : Number(row.quote.price),
      requestedAt: row.quote.updated_at ?? null,
      override: (current?.override ?? false) || override,
      policyName: current?.policyName ?? row.band?.policy?.name ?? null,
    });
  }
  return Array.from(byQuote.values()).sort((a, b) =>
    String(a.requestedAt ?? '').localeCompare(String(b.requestedAt ?? '')),
  );
}

export const usePendingAgreementApprovalsQuery = ({
  viewer,
  enabled = true,
}: {
  viewer: AgreementApprovalViewer;
  enabled?: boolean;
}): UseQueryResult<PendingAgreementApprovalItem[], Error> =>
  useQuery({
    queryKey: [...pendingAgreementApprovalsQueryKey, viewer.profileId, viewer.roleCodes.join(',')],
    enabled: enabled && viewer.isManager && Boolean(viewer.profileId),
    queryFn: async () =>
      toPendingAgreementApprovalItems(
        await fetchPendingAgreementApprovals(viewer.profileId as string),
        viewer,
      ),
    staleTime: 15000,
    refetchInterval: () => {
      if (typeof document === 'undefined') return 60000;
      return document.hidden ? false : 60000;
    },
  });
